import { useEffect, useRef, useState, type ReactNode } from 'react';
import { cn } from '@/lib/cn';

interface Props {
  children: ReactNode;
  className?: string;
}

/**
 * A scrollable region that overlays a subtle bottom-edge shadow whenever
 * there is more content below the fold. Some platforms (macOS) hide
 * scrollbars until an active scroll gesture, so a long panel with no cue
 * can look "finished" at the viewport edge when it isn't — this makes the
 * cutoff visible. The immediate parent must be `position: relative`.
 */
export function ScrollFadeArea({ children, className }: Props) {
  const ref = useRef<HTMLDivElement>(null);
  const [hasMore, setHasMore] = useState(false);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const update = () => {
      setHasMore(el.scrollHeight - el.scrollTop - el.clientHeight > 4);
    };
    update();
    el.addEventListener('scroll', update, { passive: true });
    // ResizeObserver on `el` only fires when el's own box size changes, not
    // when its *content* grows/shrinks (el's height is fixed by the flex
    // layout) — so also watch the content wrapper for height changes (e.g.
    // an example row added/removed) via its first child.
    const ro = new ResizeObserver(update);
    ro.observe(el);
    if (el.firstElementChild) ro.observe(el.firstElementChild);
    return () => {
      el.removeEventListener('scroll', update);
      ro.disconnect();
    };
  }, [children]);

  return (
    <>
      <div ref={ref} className={className}>
        {children}
      </div>
      <div
        aria-hidden
        className={cn(
          'pointer-events-none absolute inset-x-0 bottom-0 h-10 transition-opacity duration-200',
          'bg-gradient-to-t from-black/10 to-transparent',
          hasMore ? 'opacity-100' : 'opacity-0',
        )}
      />
    </>
  );
}
